/**
 * Help-panel widgets shown beside the new-space form: per-template format
 * guidance and prompt-writing tips.
 *
 * Two floating help cards rendered to the right of the form when the matching
 * field is focused. `ConversationFormatHelp` pulls its copy from the selected
 * `SpaceTemplate` (description label or full description component) and
 * optionally appends a Playbook link; `PromptHelp` is a static checklist of
 * good-prompt and bad-prompt patterns. Both wrap `FormFieldHelp`, which handles
 * the absolute positioning so callers only pass content and an optional
 * `topOffset` to align with the focused field.
 */

import { useTheme } from '@repo/theme';
import { Pad, VertBox } from 'component/basics';
import { BulletText } from 'component/richtext';
import { LinkText, UtilityText } from 'component/text';
import type { SpaceTemplate } from 'feature/editorial-dash/spaces';
import { FormFieldHelp, PLAYBOOK_URL } from 'feature/editorial-dash/spaces/form-field-help';
import { t } from 'util/stdtypes';

export type ConversationFormatHelpProps = { template: SpaceTemplate; topOffset?: number; showPlayBookLink?: boolean };

export function ConversationFormatHelp({ template, topOffset, showPlayBookLink = false }: ConversationFormatHelpProps) {
    const { colors } = useTheme();
    function renderDescription(): React.ReactNode {
        const { descriptionLabel, descriptionComponent } = template;
        if (descriptionComponent) {
            return descriptionComponent;
        }
        if (descriptionLabel) {
            return <UtilityText color={colors.colorTextSecondary} label={descriptionLabel} type='small' />;
        }
        return null;
    }

    return (
        <FormFieldHelp titleLabel={template.label} topOffset={topOffset}>
            {renderDescription()}
            {showPlayBookLink && (
                <VertBox gap={8}>
                    <Pad size={8} />
                    <UtilityText label={t('spaces.still_need_help', 'Still need help?')} type='large' weight='medium' />
                    <LinkText label={t('spaces.visit_playbook', 'Visit the PSI Playbook')} url={PLAYBOOK_URL} />
                </VertBox>
            )}
        </FormFieldHelp>
    );
}

export type PromptHelpProps = { topOffset?: number };

export function PromptHelp({ topOffset }: PromptHelpProps) {
    const { colors } = useTheme();
    return (
        <FormFieldHelp titleLabel={t('spaces.prompt_help_title', 'Need some writing help?')} topOffset={topOffset}>
            <VertBox gap={24}>
                <VertBox gap={8}>
                    <UtilityText label={t('spaces.prompt_help_good_prompts', 'Good prompts are:')} type='large' weight='medium' />
                    <VertBox gap={4}>
                        <BulletText
                            color={colors.colorTextSecondary}
                            label={t('spaces.prompt_help_tip_1', 'Timely, specific, culturally relevant')}
                        />
                        <BulletText
                            color={colors.colorTextSecondary}
                            label={t('spaces.prompt_help_tip_2', 'Open to debate and discussion')}
                        />
                        <BulletText
                            color={colors.colorTextSecondary}
                            label={t('spaces.prompt_help_tip_3', 'Clear and open-ended, but not abstract')}
                        />
                    </VertBox>
                </VertBox>
                <VertBox gap={8}>
                    <UtilityText label={t('spaces.prompt_help_avoid', 'Avoid:')} type='large' weight='medium' />
                    <VertBox gap={4}>
                        <BulletText color={colors.colorTextSecondary} label={t('spaces.prompt_help_avoid_1', 'Leading questions')} />
                        <BulletText
                            color={colors.colorTextSecondary}
                            label={t('spaces.prompt_help_avoid_2', 'Purely theoretical topics')}
                        />
                    </VertBox>
                </VertBox>
                <VertBox gap={8}>
                    <UtilityText label={t('spaces.still_need_help', 'Still need help?')} type='large' weight='medium' />
                    <LinkText label={t('spaces.visit_playbook', 'Visit the PSI Playbook')} url={PLAYBOOK_URL} />
                </VertBox>
            </VertBox>
        </FormFieldHelp>
    );
}
